import React, { useState, useRef, useEffect } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import ClubCard from './EditorCards/ClubCard';
import ContactCard from './EditorCards/ContactCard';
import AnnouncementsCard from './EditorCards/AnnouncementsCard';
import HintsManager from 'plugins/hints/HintsManager';
import PageBuilder from 'plugins/pagebuilder/PageBuilder';
import StandardModal from 'components/general/defaultModals/StandardModal';
import { registerLegacyInstanceInController } from 'services/instanceRegistration';
import { canManagePageBuilder } from 'utils/acl';
import RightSidebar from 'components/navigation/RightSidebar';

const TOOLS = [
  { key: 'club', label: 'Club Information', icon: 'bi bi-building' },
  { key: 'announcements', label: 'Announcements', icon: 'bi bi-megaphone' },
  { key: 'hints', label: 'Hints', icon: 'bi bi-lightbulb' },
  { key: 'pagebuilder', label: 'Page Builder', icon: 'bi bi-grid-1x2-fill' },
  { key: 'register-legacy', label: 'Register Legacy Instance', icon: 'bi bi-cloud-arrow-up' },
];

export default function ConfigToolbar({ homePageData, getHomePageData, clubData, getClubData, legacyRegistrationBootstrap }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerMessage, setRegisterMessage] = useState('');
  const [registerCompleted, setRegisterCompleted] = useState(false);
  const [instanceRegistered, setInstanceRegistered] = useState(false);
  const [registrationStatusReady, setRegistrationStatusReady] = useState(false);

  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const clubSaveRef = useRef(null);
  const contactSaveRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(e) {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (toggleRef.current && toggleRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!canManagePageBuilder(authUser)) {
      setRegistrationStatusReady(true);
      return undefined;
    }
    return undefined;
  }, [authUser]);

  useEffect(() => {
    if (!legacyRegistrationBootstrap) return;

    setRegistrationStatusReady(true);
    setInstanceRegistered(Boolean(legacyRegistrationBootstrap.registered));
    setRegisterMessage(legacyRegistrationBootstrap.message || '');
    setRegisterCompleted(Boolean(legacyRegistrationBootstrap.registered));
  }, [legacyRegistrationBootstrap]);

  useEffect(() => {
    if (registrationStatusReady && instanceRegistered && activeTool === 'register-legacy') {
      handleCloseModal();
    }
  }, [registrationStatusReady, instanceRegistered, activeTool]);

  if (!canManagePageBuilder(authUser)) {
    return null;
  }

  const handleToolClick = async (key) => {
    if (key === 'register-legacy') {
      setRegisterMessage('');
      setRegisterCompleted(false);
      setActiveTool(key);
      setShowModal(true);
      setMenuOpen(false);
      return;
    }

    setActiveTool(key);
    setShowModal(true);
    setMenuOpen(false);
  };

  const handleCloseModal = () => {
    clubSaveRef.current = null;
    contactSaveRef.current = null;
    setShowModal(false);
    setActiveTool(null);
  };

  const handleFooterSave = async () => {
    if (clubSaveRef.current) await clubSaveRef.current();
    if (contactSaveRef.current) await contactSaveRef.current();
    handleCloseModal();
  };

  const activeTool_ = TOOLS.find((tool) => tool.key === activeTool);
  const visibleTools = TOOLS.filter((tool) => tool.key !== 'register-legacy' || (registrationStatusReady && !instanceRegistered));

  const renderModalBody = () => {
    switch (activeTool) {
      case 'club':
        return (
          <>
            <ClubCard clubData={clubData} getClub={getClubData} saveRef={clubSaveRef} />
            <hr className="my-4" />
            <ContactCard homePageData={homePageData} getHomePageData={getHomePageData} saveRef={contactSaveRef} />
          </>
        );
      case 'announcements':
        return <AnnouncementsCard />;
      case 'hints':
        return <HintsManager />;
      case 'pagebuilder':
        return <PageBuilder />;
      case 'register-legacy':
        return (
          <div className="stack register-legacy-modal">
            <p className="mb-0 register-legacy-modal-copy">
              Register this legacy instance in tm-controller using the current frontend and backend URLs.
            </p>
            {registerMessage ? (
              <div className={registerCompleted ? 'small register-legacy-modal-message' : 'panel error-panel'}>
                {registerMessage}
              </div>
            ) : null}
            {!registerCompleted ? (
              <Button
                variant="primary"
                onClick={async () => {
                  try {
                    setRegistering(true);
                    setRegisterMessage('');
                    const result = await registerLegacyInstanceInController();
                    setRegisterMessage(result.message);
                    setRegisterCompleted(true);
                    setInstanceRegistered(true);
                    setRegistrationStatusReady(true);
                  } catch (error) {
                    setRegisterMessage(error.message || 'Registration failed');
                    setRegisterCompleted(false);
                  } finally {
                    setRegistering(false);
                  }
                }}
                disabled={registering}
              >
                {registering ? 'Registering...' : 'Register Instance'}
              </Button>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div ref={toggleRef} className="right-menu">
        <button
          className="right-menu-toggle"
          onClick={() => setMenuOpen((value) => !value)}
          aria-label="Toggle page configuration"
        >
          <i className="bi bi-sliders2"></i>
        </button>
      </div>

      <RightSidebar label="Page Configuration" open={menuOpen} onClose={() => setMenuOpen(false)} panelRef={panelRef} keepOpenOnAction>
            <div className="right-menu-header">
              <span className="right-menu-title">Page Config</span>
              <button className="right-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                &times;
              </button>
            </div>
            <div className="right-menu-items">
              {visibleTools.map((tool) => (
                <button
                  key={tool.key}
                  className="right-menu-item"
                  onClick={() => handleToolClick(tool.key)}
                  disabled={registering && tool.key === 'register-legacy'}
                >
                  <i className={tool.icon}></i>
                  <span>
                    {tool.key === 'register-legacy' && registering ? 'Registering...' : tool.label}
                  </span>
                </button>
              ))}
            </div>
      </RightSidebar>

      <StandardModal
        navigationParent="configuration"
        show={showModal}
        handleClose={handleCloseModal}
        title={activeTool_?.label}
        style={{ maxWidth: '95%', marginTop: '4vh' }}
        ModalBodyComponent={<Container>{renderModalBody()}</Container>}
        ModalFooterComponent={activeTool === 'pagebuilder' || activeTool === 'register-legacy'
          ? null
          : (activeTool === 'hints' || activeTool === 'announcements')
            ? <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
            : (
              <Button variant="primary" onClick={handleFooterSave}>
                <i className="bi bi-floppy me-1" /> Save
              </Button>
            )}
      />
    </>
  );
}
